import { type Role, type User, type UserRoleNodeMapping, type UserRoleNodeMappingPayload } from '@ekkogmbh/apisdk';
import { DatePicker } from '@mui/x-date-pickers';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CoordinateInput } from 'src/Common/Components/CoordinateInput';
import { ContentPanel, usePanel } from 'src/Common/Components/ContentControl/ContentPanel';
import { ReactivePanelColumn } from 'src/Common/Components/Stacks';
import useRequest from 'src/Common/Hooks/useRequest';
import useEkko from 'src/Common/Providers/EkkoProvider';
import UserPicker from '../UserPicker';
import RolePicker from 'src/RoleManagement/RolePicker';
import usePagination from 'src/Common/Providers/PaginationProvider';

interface Props {
  user?: User;
  role?: Role;
  mapping?: UserRoleNodeMapping;
  closeHandler: () => void;
}

export const UserMappingPanel = ({ user, role, mapping, closeHandler }: Props): React.JSX.Element => {
  const { api } = useEkko();
  const { refresh } = usePagination();
  const { enableSave } = usePanel();
  const [isSaving, startSaving] = useRequest();
  const isEditMode = useMemo(() => mapping !== undefined, [mapping]);

  const [coordinate, setCoordinate] = useState<string>(mapping?.node.coordinate ?? '');
  const [validUntil, setValidUntil] = useState<string>(
    (mapping?.validUntil ? moment(mapping.validUntil) : moment().add(1, 'd')).format('YYYY-MM-DD'),
  );
  const [selectedRole, setSelectedRole] = useState<Role | undefined>(mapping?.role ?? role);
  const [selectedUser, setSelectedUser] = useState<User | undefined>(mapping?.user ?? user);

  useEffect(() => {
    if (role === undefined) {
      return;
    }

    if (role.coordinate.startsWith(coordinate) || coordinate.startsWith(role.coordinate)) {
      return;
    }

    setCoordinate(role.coordinate);
  }, [role, coordinate]);

  /** all filled handler */
  useEffect(() => {
    const hasUser = selectedUser !== undefined;
    const hasRole = selectedRole !== undefined;
    const isValidUntilSet = (selectedUser && selectedUser.automation) || validUntil !== '';

    enableSave(hasUser && hasRole && coordinate !== '' && isValidUntilSet);
  }, [selectedRole, selectedUser, coordinate, validUntil]);

  const saveMapping = useCallback(() => {
    const payload: UserRoleNodeMappingPayload = {
      user: selectedUser!,
      role: selectedRole!,
      coordinate,
      validUntil,
    };
    startSaving(
      () => {
        if (isEditMode && mapping !== undefined) {
          return api.updateUserRoleNodeMapping({
            ...mapping,
            ...payload,
          });
        } else {
          return api.addUserRoleNodeMapping(payload);
        }
      },
      () => {
        refresh();
        closeHandler();
      },
    );
  }, [selectedRole, selectedUser, coordinate, validUntil, mapping, isEditMode]);

  const reset = useCallback(() => {
    setCoordinate(mapping?.node.coordinate ?? '');
    setValidUntil((mapping?.validUntil ? moment(mapping.validUntil) : moment().add(1, 'd')).format('YYYY-MM-DD'));
    setSelectedUser(mapping?.user ?? user);
    setSelectedRole(mapping?.role ?? role);
  }, [mapping, user, role]);

  return (
    <ContentPanel
      isLoading={isSaving}
      steps={[
        {
          title: 'User-Role-Mapping',
          contentCallback: () => (
            <ReactivePanelColumn>
              <UserPicker disabled={user !== undefined} user={selectedUser} onChange={(u) => setSelectedUser(u)} />
              <RolePicker
                disabled={role !== undefined}
                role={selectedRole}
                onChange={(r) => setSelectedRole(r as Role | undefined)}
              />
              <CoordinateInput onChange={setCoordinate} value={coordinate} trailingDelimiter={false} />
              {selectedUser && !selectedUser.automation && (
                <DatePicker
                  label="Valid-Until"
                  disablePast
                  value={moment(validUntil)}
                  onChange={(date) => {
                    if (date === null) {
                      setValidUntil('');
                    } else {
                      setValidUntil(date.format('YYYY-MM-DD'));
                    }
                  }}
                  minDate={moment().add(1, 'd')}
                  sx={{
                    width: '100%',
                  }}
                />
              )}
            </ReactivePanelColumn>
          ),
        },
      ]}
      onCancel={closeHandler}
      onReset={reset}
      onSave={saveMapping}
    />
  );
};
